import { z } from 'zod';

const ratingSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  value: z.number(),
  color: z.string(),
});

export const riskAssessmentResultConfigSchema = z.object({
  likelihood: z.object({
    ratings: z.array(ratingSchema),
  }),
  impact: z.object({
    ratings: z.array(ratingSchema),
    categories: z.array(z.object({ name: z.string(), color: z.string() })),
    aggregation: z.enum(['average', 'maximum']).optional(),
  }),
  matrix: z.array(
    z.object({
      title: z.string(),
      value: z.number(),
      color: z.string(),
      likelihood: z.number(),
      impact: z.number(),
    })
  ),
});

export type RiskAssessmentResultConfig = z.infer<
  typeof riskAssessmentResultConfigSchema
>;

type DefaultRatingValue = 1 | 2 | 3 | 4 | 5;

// Rating title + colour by result value. Colours match the default taxonomy
// so ratings render identically.
const DEFAULT_RATING_BY_VALUE: Record<
  DefaultRatingValue,
  { title: string; color: string }
> = {
  1: { title: 'Minimal', color: '#6DAC3F' },
  2: { title: 'Low', color: '#8CC862' },
  3: { title: 'Moderate', color: '#F2A041' },
  4: { title: 'High', color: '#E37373' },
  5: { title: 'Critical', color: '#CE1B1B' },
};

// Result rating value per [likelihood][impact] (both 1-5).
const DEFAULT_MATRIX_VALUES: DefaultRatingValue[][] = [
  [1, 2, 2, 2, 2],
  [2, 2, 2, 3, 3],
  [2, 2, 3, 3, 4],
  [2, 3, 3, 4, 4],
  [2, 3, 4, 4, 5],
];

/**
 * Default risk scoring config, used when the scoring-settings flag is on but an
 * org has no stored config. Reproduces the default taxonomy ratings exactly, so
 * resolving against it changes nothing visible. Deliberately taxonomy-free so it
 * survives removal of the i18n taxonomy — do not derive it from that.
 */
export const DEFAULT_RISK_ASSESSMENT_RESULT_CONFIG: RiskAssessmentResultConfig =
  {
    likelihood: {
      ratings: [
        { title: 'Very Unlikely', value: 1, color: '#6DAC3F' },
        { title: 'Unlikely', value: 2, color: '#8CC862' },
        { title: 'Likely', value: 3, color: '#F2A041' },
        { title: 'Very Likely', value: 4, color: '#E37373' },
        { title: 'Certain', value: 5, color: '#CE1B1B' },
      ],
    },
    impact: {
      ratings: [
        { title: 'Minimal', value: 1, color: '#6DAC3F' },
        { title: 'Low', value: 2, color: '#8CC862' },
        { title: 'Moderate', value: 3, color: '#F2A041' },
        { title: 'High', value: 4, color: '#E37373' },
        { title: 'Critical', value: 5, color: '#CE1B1B' },
      ],
      categories: [],
    },
    matrix: DEFAULT_MATRIX_VALUES.flatMap((likelihoodRow, likelihoodIndex) =>
      likelihoodRow.map((value, impactIndex) => ({
        ...DEFAULT_RATING_BY_VALUE[value],
        value,
        likelihood: likelihoodIndex + 1,
        impact: impactIndex + 1,
      }))
    ),
  };
